import React, { useContext, useEffect, useMemo, useState } from 'react'
import DatePicker from 'react-datepicker'
import Select from 'react-select'
import UserContext from '../../UserContext'
import { ApiFetch } from '../Common/Api'
import { SearchOpportunitiesFilter, SearchOpportunityStatus } from './types'

type SearchOpportunitiesBarProps = {
    criteria: SearchOpportunitiesFilter
    onSearch: (criteria: SearchOpportunitiesFilter) => void
    loading?: boolean
}

type FilterOption = {
    value: string
    label: string
}

type ActiveFilterChip = {
    id: string
    label: string
    value: string
}

const formatChipDate = (value: Date | null | undefined): string => {
    if (!value) return ''
    const date = new Date(value)
    const month = `${date.getMonth() + 1}`.padStart(2, '0')
    const day = `${date.getDate()}`.padStart(2, '0')
    const year = `${date.getFullYear()}`
    return `${month}/${day}/${year}`
}

const defaultStatus: SearchOpportunityStatus[] = ['All']
const statusOptions: FilterOption[] = [
    { value: 'All', label: 'All' },
    { value: 'Active', label: 'Active' },
    { value: 'Expired', label: 'Expired' }
]

const normalizeStatusSelection = (values: SearchOpportunityStatus[]): SearchOpportunityStatus[] => {
    if (!values.length) return defaultStatus
    const withoutAll = values.filter((status) => status !== 'All')
    return withoutAll.length ? withoutAll : defaultStatus
}

const normalizeDate = (value: Date | null | undefined): Date | null => {
    if (!value) return null
    const next = new Date(value)
    next.setHours(0, 0, 0, 0)
    return next
}

const todayAtMidnight = (): Date => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
}

const SearchOpportunitiesBar: React.FC<SearchOpportunitiesBarProps> = ({ criteria, onSearch, loading = false }) => {
    const { cachedGet } = useContext(UserContext)
    const { CacheLookupsMinutes } = window.CS_CONFIG
    const [stateOptions, setStateOptions] = useState<string[]>([])
    const [serviceTypeOptions, setServiceTypeOptions] = useState<string[]>([])
    const [workingCriteria, setWorkingCriteria] = useState<SearchOpportunitiesFilter>(criteria)
    const [dateError, setDateError] = useState<string>('')

    useEffect(() => {
        setWorkingCriteria({
            ...criteria,
            endDate: criteria.startDate && !criteria.endDate ? todayAtMidnight() : criteria.endDate
        })
    }, [criteria])

    useEffect(() => {
        cachedGet('ApiStatesLookup_SearchOpportunities', CacheLookupsMinutes, () => ApiFetch<string[]>('County/States', {
            restrictedCounties: false,
            includeAdjacent: true
        }))
            .then((result) => setStateOptions(result || []))
            // eslint-disable-next-line no-console
            .catch(console.log)

        cachedGet('ApiLookupSpecialtyServicesCategories', CacheLookupsMinutes, () => ApiFetch<string[]>('Lookup', { key: 'SpecialtyServicesCategories' }))
            .then((result) => setServiceTypeOptions(result || []))
            // eslint-disable-next-line no-console
            .catch(console.log)
    }, [cachedGet, CacheLookupsMinutes])

    const mappedStateOptions = useMemo<FilterOption[]>(() => stateOptions.map((state) => ({ value: state, label: state })), [stateOptions])
    const mappedServiceTypeOptions = useMemo<FilterOption[]>(() => serviceTypeOptions.map((serviceType) => ({ value: serviceType, label: serviceType })), [serviceTypeOptions])

    const updateValue = <K extends keyof SearchOpportunitiesFilter>(key: K, value: SearchOpportunitiesFilter[K]) => {
        setWorkingCriteria((prev) => ({ ...prev, [key]: value }))
    }

    const handleStartDateChange = (value: Date | null) => {
        const normalizedStartDate = normalizeDate(value)

        setWorkingCriteria((prev) => ({
            ...prev,
            startDate: normalizedStartDate,
            endDate: normalizedStartDate && !prev.endDate ? todayAtMidnight() : prev.endDate
        }))
    }

    const selectedStateOptions = useMemo(() => (workingCriteria.countiesState || []).map((value) => ({ value, label: value })), [workingCriteria.countiesState])
    const selectedServiceTypeOptions = useMemo(() => (workingCriteria.serviceType || []).map((value) => ({ value, label: value })), [workingCriteria.serviceType])
    const selectedStatusOptions = useMemo(() => (workingCriteria.status || defaultStatus)
        .filter((value) => value !== 'All')
        .map((value) => ({ value, label: value })), [workingCriteria.status])

    const minSearchDate = useMemo(() => {
        const sixMonthsAgo = new Date(todayAtMidnight())
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)
        return sixMonthsAgo
    }, [])

    const validateSearchDates = (searchCriteria: SearchOpportunitiesFilter): string | null => {
        const start = normalizeDate(searchCriteria.startDate)
        const end = normalizeDate(searchCriteria.endDate)

        if ((start && start < minSearchDate) || (end && end < minSearchDate)) {
            return `Dates cannot be earlier than ${formatChipDate(minSearchDate)}.`
        }

        if (start && !end) {
            return 'End date is required when start date is set.'
        }

        if (start && end) {
            const rangeDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24))

            if (rangeDays < 0) {
                return 'End date must be after start date.'
            }

            if (rangeDays > 183) {
                return 'Date range cannot exceed 6 months.'
            }
        }

        return null
    }

    const submit = () => {
        const validationError = validateSearchDates(workingCriteria)
        if (validationError) {
            setDateError(validationError)
            return
        }

        setDateError('')
        onSearch({
            ...workingCriteria,
            status: normalizeStatusSelection(workingCriteria.status || defaultStatus)
        })
    }

    const clear = () => {
        const cleared: SearchOpportunitiesFilter = {
            countiesState: [],
            locationText: '',
            serviceType: [],
            startDate: null,
            endDate: null,
            status: defaultStatus
        }

        setDateError('')
        setWorkingCriteria(cleared)
        onSearch(cleared)
    }

    const activeFilterChips = useMemo(() => {
        const chips: ActiveFilterChip[] = []

        if ((workingCriteria.locationText || '').trim()) {
            chips.push({
                id: 'locationText',
                label: 'Location',
                value: (workingCriteria.locationText || '').trim()
            })
        }

        const startDateText = formatChipDate(workingCriteria.startDate)
        const endDateText = formatChipDate(workingCriteria.endDate)
        if (startDateText || endDateText) {
            const dateRange = startDateText && endDateText
                ? `${startDateText} - ${endDateText}`
                : startDateText
                    ? `From ${startDateText}`
                    : `Until ${endDateText}`

            chips.push({
                id: 'dateRange',
                label: 'Date Range',
                value: dateRange
            })
        }

        const selectedStatuses = (workingCriteria.status || []).filter((status) => status !== 'All')
        selectedStatuses.forEach((status) => chips.push({ id: `status:${status}`, label: 'Status', value: status }))

        const selectedServiceTypes = workingCriteria.serviceType || []
        selectedServiceTypes.forEach((serviceType) => chips.push({ id: `serviceType:${serviceType}`, label: 'Service Type', value: serviceType }))

        const selectedStates = workingCriteria.countiesState || []
        selectedStates.forEach((state) => chips.push({ id: `state:${state}`, label: 'State', value: state }))

        return chips
    }, [workingCriteria.locationText, workingCriteria.startDate, workingCriteria.endDate, workingCriteria.status, workingCriteria.serviceType, workingCriteria.countiesState])

    const applyCriteriaAndSearch = (nextCriteria: SearchOpportunitiesFilter) => {
        const validationError = validateSearchDates(nextCriteria)
        if (validationError) {
            setDateError(validationError)
            return
        }

        setDateError('')
        setWorkingCriteria(nextCriteria)
        onSearch({
            ...nextCriteria,
            status: normalizeStatusSelection(nextCriteria.status || defaultStatus)
        })
    }

    const removeChip = (chipId: string) => {
        const [group, value] = chipId.split(':')

        if (chipId === 'locationText') {
            applyCriteriaAndSearch({ ...workingCriteria, locationText: '' })
            return
        }

        if (chipId === 'dateRange') {
            applyCriteriaAndSearch({
                ...workingCriteria,
                startDate: null,
                endDate: null
            })
            return
        }

        if (group === 'status') {
            const nextStatus = normalizeStatusSelection((workingCriteria.status || []).filter((status) => status !== value))
            applyCriteriaAndSearch({ ...workingCriteria, status: nextStatus })
            return
        }

        if (group === 'serviceType') {
            applyCriteriaAndSearch({
                ...workingCriteria,
                serviceType: (workingCriteria.serviceType || []).filter((serviceType) => serviceType !== value)
            })
            return
        }

        if (group === 'state') {
            applyCriteriaAndSearch({
                ...workingCriteria,
                countiesState: (workingCriteria.countiesState || []).filter((state) => state !== value)
            })
        }
    }

    return (
        <div className='search-opportunities-bar'>
            <div className='search-opportunities-row search-opportunities-row-top'>
                <div className='search-opportunities-field search-opportunities-field-location'>
                    <label className='search-opportunities-field-label'>Zip / County / Region</label>
                    <input
                        type='text'
                        placeholder='Enter zip, county, or region'
                        value={workingCriteria.locationText || ''}
                        onChange={(e) => updateValue('locationText', e.target.value)}
                        className='search-opportunities-text'
                    />
                </div>

                <div className='search-opportunities-field'>
                    <div className='search-opportunities-field-label'>Creation Date Range</div>
                    <label className='search-opportunities-field-label'>Start Date</label>
                    <DatePicker
                        selected={workingCriteria.startDate ? new Date(workingCriteria.startDate) : null}
                        onChange={(value) => handleStartDateChange(value as Date | null)}
                        minDate={minSearchDate}
                        placeholderText='Start date'
                        dateFormat={['MM/dd/yyyy', 'MMddyyyy', 'yyyyMMdd']}
                        className='search-opportunities-date'
                    />
                </div>

                <div className='search-opportunities-field'>
                    <label className='search-opportunities-field-label'>End Date</label>
                    <DatePicker
                        selected={workingCriteria.endDate ? new Date(workingCriteria.endDate) : null}
                        onChange={(value) => updateValue('endDate', value as Date | null)}
                        minDate={
                            workingCriteria.startDate && new Date(workingCriteria.startDate) > minSearchDate
                                ? new Date(workingCriteria.startDate)
                                : minSearchDate
                        }
                        required={!!workingCriteria.startDate}
                        placeholderText='End date'
                        dateFormat={['MM/dd/yyyy', 'MMddyyyy', 'yyyyMMdd']}
                        className='search-opportunities-date'
                    />
                </div>
            </div>

            <div className='search-opportunities-row search-opportunities-row-bottom'>
                <div className='search-opportunities-field search-opportunities-field-select'>
                    <label className='search-opportunities-field-label'>State</label>
                    <div className='search-opportunities-select-wrapper'>
                        <Select<FilterOption, true>
                            inputId='search-opportunities-state'
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            className='search-opportunities-react-select'
                            classNamePrefix='react-select'
                            placeholder='All states'
                            options={mappedStateOptions}
                            value={selectedStateOptions}
                            onChange={(values) => updateValue('countiesState', ((values || []) as FilterOption[]).map((option) => option.value))}
                        />
                    </div>
                </div>

                <div className='search-opportunities-field search-opportunities-field-select'>
                    <label className='search-opportunities-field-label'>Service Type</label>
                    <div className='search-opportunities-select-wrapper'>
                        <Select<FilterOption, true>
                            inputId='search-opportunities-service-type'
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            className='search-opportunities-react-select'
                            classNamePrefix='react-select'
                            placeholder='All service types'
                            options={mappedServiceTypeOptions}
                            value={selectedServiceTypeOptions}
                            onChange={(values) => updateValue('serviceType', ((values || []) as FilterOption[]).map((option) => option.value))}
                        />
                    </div>
                </div>

                <div className='search-opportunities-field search-opportunities-field-select'>
                    <label className='search-opportunities-field-label'>Status</label>
                    <div className='search-opportunities-select-wrapper'>
                        <Select<FilterOption, true>
                            inputId='search-opportunities-status'
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            className='search-opportunities-react-select'
                            classNamePrefix='react-select'
                            placeholder='All statuses'
                            options={statusOptions}
                            value={selectedStatusOptions}
                            onChange={(values) => {
                                const selected = ((values || []) as FilterOption[]).map((option) => option.value as SearchOpportunityStatus)
                                updateValue('status', normalizeStatusSelection(selected))
                            }}
                        />
                    </div>
                </div>

                <div className='search-opportunities-actions'>
                    <button type='button' className='button small search-opportunities-button' onClick={submit} disabled={loading}>
                        Search
                    </button>

                    <button type='button' className='button clear small search-opportunities-button' onClick={clear} disabled={loading}>
                        Clear
                    </button>
                </div>
            </div>

            <div className='search-opportunities-active-filters'>
                <span className='active-filters-label'>ACTIVE FILTERS:</span>
                {activeFilterChips.map((chip) => <button
                    key={chip.id}
                    type='button'
                    className='search-opportunities-filter-chip'
                    onClick={() => removeChip(chip.id)}
                >
                    {chip.label}: {chip.value}
                    <span className='search-opportunities-filter-chip-close'>x</span>
                </button>)}

                {activeFilterChips.length > 0 && <button
                    type='button'
                    className='search-opportunities-filter-clear-all'
                    onClick={() => {
                        const clearedCriteria: SearchOpportunitiesFilter = {
                            ...workingCriteria,
                            locationText: '',
                            startDate: null,
                            endDate: null,
                            status: defaultStatus,
                            serviceType: [],
                            countiesState: [],
                        }

                        updateValue('locationText', '')
                        updateValue('startDate', null)
                        updateValue('endDate', null)
                        updateValue('status', defaultStatus)
                        updateValue('serviceType', [])
                        updateValue('countiesState', [])
                        onSearch(clearedCriteria)
                    }}
                >
                    Clear all
                </button>}
            </div>

            {dateError && <div className='errorText search-opportunities-error'>{dateError}</div>}
        </div>
    )
}

export default SearchOpportunitiesBar
